import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EntityType } from '@miq/domain';
import { UpdateData } from '@records/state/current-data/update-data.action';
import { ExecutableAction } from '../utils/executable-action';
import { ClearCurrentData } from './clear-current-data.action';
import { GetRecord } from './get-record.action';
import { AddPropertyTypesAction } from './property/add-property-types.action';
import { SetAssociatedPropertyAction } from './set-associated-property.action';

export interface AssociatedRecord {
  type: EntityType;
  data: Record<string, any>;
}

/** Current Record data state model */
export interface CurrentDataModel {
  /** Indicates if the record data has been loaded */
  recordLoaded: boolean;
  /**
   * Array that contains the existing property usages. This data is usually
   * retrieved using the {@link processUsages} function.
   *
   * @see processUsages
   */
  usagesInfo: Record<string, any>[];
  /** Record data */
  record: Record<string, any>;

  associatedRecords?: AssociatedRecord[];
}

@State<CurrentDataModel>({
  name: 'currentData',
  defaults: {
    recordLoaded: false,
    usagesInfo: null,
    record: null,
  },
})
@Injectable()
export class CurrentDataState {
  constructor(private store: Store) {}

  @Selector()
  static record(state: CurrentDataModel) {
    return state.record;
  }

  @Selector()
  static associatedProperty(state: CurrentDataModel) {
    return state.associatedRecords.find(assoc => assoc.type === EntityType.Property)?.data ?? null;
  }

  @Action(ClearCurrentData)
  @Action(GetRecord)
  @Action(UpdateData)
  @Action(AddPropertyTypesAction)
  @Action(SetAssociatedPropertyAction)
  public executeAction(ctx: StateContext<CurrentDataModel>, params: ExecutableAction<CurrentDataModel>) {
    return params.execute({ ctx, store: this.store });
  }
}
